import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { AnnotationAvatarButton } from "~/components/Annotations/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/AnnotationAvatarButton";
import { AnnotationComment } from "~/components/Annotations/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/AnnotationComment";
import { getAnnotationCommentPos } from "~/components/Annotations/shared/annotationUtils";
import { useDiscussionPanelContextSelector } from "~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext";
import { BOUNDING_BOX_AVATAR_SIZE } from "~/components/BoundingBox/components/BoundingBoxAvatar";
import { useBoundingBoxTextPosition } from "~/components/BoundingBox/hooks/useBoundingBoxTextPosition";
import { usePopperContainer } from "~/hooks/usePopperContainer";
import { isViewingDiscussionWithId } from "~/store/router/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
export const AnnotationAvatarWithComment = /*#__PURE__*/ memo((param)=>{
    let { containerHeight, containerLeft, containerWidth, containerTop, onAvatarClick, discussion, canResolve, canReply, canShowMentionsButton, canEdit, canDelete } = param;
    const containerRef = useRef(null);
    const isActive = useAirSelector((st)=>isViewingDiscussionWithId(st, discussion.id));
    const isReplyingCurrentDiscussionIdSelector = useCallback((state)=>state.replyingDiscussionId === discussion.id, [
        discussion.id
    ]);
    const isReplyingToDiscussion = useDiscussionPanelContextSelector(isReplyingCurrentDiscussionIdSelector);
    const { minX, maxX, maxY } = useMemo(()=>getAnnotationCommentPos(discussion.annotation), [
        discussion.annotation
    ]);
    const { offsetLeft, offsetBottom, position } = useBoundingBoxTextPosition({
        startX: minX,
        endY: maxY,
        endX: maxX,
        containerLeft,
        containerWidth,
        containerHeight
    });
    const popperOptions = useMemo(()=>({
            modifiers: [
                {
                    placement: "left-start",
                    name: "offset",
                    options: {
                        offset: (param)=>{
                            let { popper: { width } } = param;
                            return [
                                position.includes("left") ? -width / 2 - 26 : width / 2 + 26,
                                -BOUNDING_BOX_AVATAR_SIZE - 8
                            ];
                        }
                    }
                }
            ]
        }), [
        position
    ]);
    const buttonPos = useMemo(()=>{
        if (position.includes("left")) {
            return {
                left: offsetLeft - BOUNDING_BOX_AVATAR_SIZE + 2,
                bottom: offsetBottom
            };
        } else {
            return {
                left: offsetLeft + 1,
                bottom: offsetBottom
            };
        }
    }, [
        offsetBottom,
        offsetLeft,
        position
    ]);
    const { PopperContainer, popperInstance } = usePopperContainer({
        containerRef,
        usePortal: true,
        options: popperOptions
    });
    useEffect(()=>{
        var _popperInstance_current;
        popperInstance === null || popperInstance === void 0 ? void 0 : (_popperInstance_current = popperInstance.current) === null || _popperInstance_current === void 0 ? void 0 : _popperInstance_current.update();
    }, [
        containerHeight,
        containerWidth,
        containerLeft,
        popperInstance,
        containerTop
    ]);
    const comment = useMemo(()=>isActive && !isReplyingToDiscussion ? /*#__PURE__*/ _jsx(PopperContainer, {
            children: /*#__PURE__*/ _jsx(AnnotationComment, {
                canEdit: canEdit,
                canReply: canReply,
                canResolve: canResolve,
                canDelete: canDelete,
                canShowMentionsButton: canShowMentionsButton,
                discussion: discussion,
                position: position
            })
        }) : null, [
        PopperContainer,
        canDelete,
        canEdit,
        canReply,
        canResolve,
        canShowMentionsButton,
        discussion,
        isActive,
        isReplyingToDiscussion,
        position
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "absolute flex",
        ref: containerRef,
        style: {
            ...buttonPos,
            pointerEvents: "all"
        },
        /**
         * Stop click propagation to prevent the popper from closing when clicking
         * on any the menu items. This is because the menu are rendered inside a
         * portal and the popper is rendered inside the annotation container.
         */ onClick: (event)=>event.stopPropagation(),
        onMouseDown: (event)=>event.stopPropagation(),
        children: [
            position.includes("left") && comment,
            /*#__PURE__*/ _jsx(AnnotationAvatarButton, {
                discussion: discussion,
                onAvatarClick: onAvatarClick,
                position: position
            }),
            position.includes("right") && comment
        ]
    });
});
AnnotationAvatarWithComment.displayName = "AnnotationAvatarWithComment";
